import React, { useEffect, useRef } from 'react';
import { X, MapPin, Calendar, Download, RefreshCw, AlertTriangle, Tag, ShoppingBag, Clock, Package, Truck, CheckCircle, RotateCcw, AlertCircle, CreditCard, Mail, Phone, Printer, Zap, Shield } from 'lucide-react';
import OrderStatusBadge from './OrderStatusBadge';

const OrderDetailsModal = ({
  isOpen,
  onClose,
  order,
  onReorder,
  onDownloadInvoice,
  onTrackOrder
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const calculateSubtotal = () => {
    return order?.items.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  };

  // Normalize order function to map existing order structure to invoice format
  const normalizeOrder = (o) => {
    if (!o) return {};

    return {
      id: o.id,
      orderNumber: o.orderNumber,
      date: o.date,
      status: o.status,
      rawStatus: o.status,
      total: o.total || 0,
      subtotal: o.subtotal || calculateSubtotal(),
      shipping: o.shippingCharges || 0,
      tax: o.tax || 0,
      payment_method: o.paymentMethod || 'COD',
      paymentMethod: o.paymentMethod || 'COD',
      items: (o.items || []).map(item => ({
        id: item.id,
        name: item.name,
        category: item.category,
        price: item.price || 0,
        quantity: item.quantity || 1,
        variant: item.customization ? Object.entries(item.customization).map(([key, value]) => `${key}: ${value}`).join(', ') : null,
        isCustom: item.customization && Object.keys(item.customization).length > 0,
        productType: item.customization && Object.keys(item.customization).length > 0 ? 'custom' : 'standard'
      })),
      customer: {
        name: o.billingAddress ? `${o.billingAddress.firstName} ${o.billingAddress.lastName}`.trim() :
              o.shippingAddress?.name || 'Customer',
        email: o.billingAddress?.email || '',
        phone: o.billingAddress?.phone || ''
      },
      shippingAddress: o.shippingAddress ? {
        street: o.shippingAddress.street || '',
        city: o.shippingAddress.city || '',
        state: o.shippingAddress.state || '',
        zipCode: o.shippingAddress.zipCode || '',
        country: 'India'
      } : null,
      isCustom: (o.items || []).some(item =>
        item.customization && Object.keys(item.customization).length > 0 ||
        /photo|custom|print|mug|keychain|personali/i.test(item.category || "") ||
        /photo|custom|print|mug|keychain|personali/i.test(item.name || "")
      ),
      productType: (o.items || []).some(item =>
        item.customization && Object.keys(item.customization).length > 0 ||
        /photo|custom|print|mug|keychain|personali/i.test(item.category || "") ||
        /photo|custom|print|mug|keychain|personali/i.test(item.name || "")
      ) ? 'custom' : 'standard'
    };
  };

  // Invoice generation function
  const openInvoice = (o) => {
    if (!o) return;
    const norm     = normalizeOrder(o);
    const subtotal = norm.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0;
    const shipping = norm.shipping ?? 0;
    const tax      = norm.tax      ?? 0;
    const total    = norm.total    ?? subtotal + shipping + tax;

    const fmt = (ds) =>
      ds ? new Date(ds).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }) : "—";

    const isCustom =
      norm.isCustom === true ||
      norm.productType === "custom" ||
      (norm.items || []).some(
        (i) =>
          i.isCustom === true ||
          i.productType === "custom" ||
          /photo|custom|print|mug|keychain|personali/i.test(i.category || "") ||
          /photo|custom|print|mug|keychain|personali/i.test(i.name || "")
      );

    const rows = (norm.items || []).map((item) => `
      <tr>
        <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6">
          ${item.name}
          ${item.isCustom || item.productType === "custom"
            ? `<span style="font-size:10px;color:#6366f1;font-weight:600;margin-left:6px;padding:1px 6px;background:#eef2ff;border-radius:10px">Custom</span>`
            : ""}
          ${item.variant ? `<br/><span style="font-size:11px;color:#9ca3af">${item.variant}</span>` : ""}
        </td>
        <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:center">${item.quantity}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:right">₹${item.price?.toFixed(2)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600">₹${(item.price * item.quantity).toFixed(2)}</td>
      </tr>`).join("");

    const summaryRows = [["Subtotal", subtotal], ["Tax", tax]].map(([l, v]) => `
      <div style="display:flex;justify-content:space-between;padding:6px 0;font-size:14px;color:#6b7280;border-bottom:1px solid #f3f4f6">
        <span>${l}</span><span style="font-weight:500;color:#374151">₹${Number(v).toFixed(2)}</span>
      </div>`).join("");

    const rawStatus     = norm.rawStatus || norm.status || "";
    const isRefunded    = /refund/i.test(rawStatus);
    const isReplacement = /replacement/i.test(rawStatus);
    const statusLabel   = isReplacement ? "Replacement" : isRefunded ? "Refunded" : "Delivered";
    const statusColor   = isRefunded || isReplacement ? "#16a34a" : "#065f46";
    const statusBg      = isRefunded || isReplacement ? "#dcfce7" : "#d1fae5";

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Invoice ${norm.orderNumber}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111827;background:#fff;padding:40px}@media print{body{padding:20px}}</style>
</head><body><div style="max-width:720px;margin:0 auto">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;padding-bottom:24px;border-bottom:2px solid #0d9488">
      <div>
        <h1 style="font-size:26px;font-weight:800;color:#0d9488">INVOICE</h1>
        <p style="color:#6b7280;font-size:13px;margin-top:4px">${norm.orderNumber}</p>
        ${isCustom ? `<span style="display:inline-block;margin-top:6px;padding:2px 8px;background:#eef2ff;color:#6366f1;border-radius:10px;font-size:11px;font-weight:700">✦ Custom Print Order</span>` : ""}
      </div>
      <div style="text-align:right">
        <p style="font-size:13px;color:#6b7280">Date</p>
        <p style="font-weight:600;margin-top:2px">${fmt(norm.date)}</p>
        <p style="font-size:13px;color:#6b7280;margin-top:10px">Payment</p>
        <p style="font-weight:600;margin-top:2px">${norm.payment_method || norm.paymentMethod || "COD"}</p>
        <span style="display:inline-block;margin-top:8px;padding:4px 12px;background:${statusBg};color:${statusColor};border-radius:20px;font-size:12px;font-weight:700">${statusLabel}</span>
      </div>
    </div>
    <div style="background:#f9fafb;border-radius:12px;padding:20px;margin-bottom:28px;border:1px solid #e5e7eb">
      <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;margin-bottom:10px">Bill To</p>
      <p style="font-weight:700;font-size:15px">${norm.customer?.name || "Customer"}</p>
      ${norm.customer?.email  ? `<p style="color:#6b7280;font-size:13px;margin-top:3px">${norm.customer.email}</p>` : ""}
      ${norm.customer?.phone  ? `<p style="color:#6b7280;font-size:13px;margin-top:2px">${norm.customer.phone}</p>` : ""}
      ${norm.shippingAddress  ? `<p style="color:#6b7280;font-size:13px;margin-top:6px">${[norm.shippingAddress.street, norm.shippingAddress.city, norm.shippingAddress.state, norm.shippingAddress.zipCode, norm.shippingAddress.country].filter(Boolean).join(", ")}</p>` : ""}
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:24px">
      <thead><tr style="background:#f3f4f6">
        <th style="padding:10px 12px;text-align:left;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Item</th>
        <th style="padding:10px 12px;text-align:center;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Qty</th>
        <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Price</th>
        <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Total</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin-bottom:36px">
      <div style="width:260px">
        ${summaryRows}
        ${shipping ? `<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:14px;color:#6b7280;border-bottom:1px solid #f3f4f6"><span>Shipping</span><span style="font-weight:500;color:#374151">₹${Number(shipping).toFixed(2)}</span></div>` : ""}
        <div style="display:flex;justify-content:space-between;padding:12px 0 4px;font-size:16px;font-weight:800">
          <span>Total</span><span style="color:#0d9488">₹${Number(total).toFixed(2)}</span>
        </div>
      </div>
    </div>
    ${isCustom ? `<div style="background:#eef2ff;border:1px solid #c7d2fe;border-radius:12px;padding:16px;margin-bottom:24px;font-size:12px;color:#4338ca">
      <strong>Custom Print Order Notice:</strong> This order was processed as a custom print product. Returns are only accepted for valid defect or damage claims. See our return policy for details.
    </div>` : ""}
    <div style="border-top:1px solid #e5e7eb;padding-top:20px;text-align:center;color:#9ca3af;font-size:12px">
      <p>Thank you for your order! 🎉</p>
      <p style="margin-top:4px">For any queries, please contact our support team.</p>
    </div>
  </div>
  <script>window.onload=()=>window.print();</script>
  </body></html>`;

    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  // Handle download invoice
  const handleDownloadInvoice = () => {
    if (order) {
      openInvoice(order);
    }
  };

  const getTimelineIcon = (status) => {
    // Normalize status to handle case variations and underscores
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedStatus) {
      case 'placed':
        return <ShoppingBag className="w-4 h-4" />;
      case 'processing':
        return <ShoppingBag className="w-4 h-4" />;
      case 'printing_started':
      case 'printing started':
        return <Printer className="w-4 h-4" />;
      case 'production_completed':
      case 'production completed':
        return <Zap className="w-4 h-4" />;
      case 'quality_check':
      case 'quality check':
        return <Shield className="w-4 h-4" />;
      case 'packaging':
        return <ShoppingBag className="w-4 h-4" />;
      case 'shipped':
        return <Truck className="w-4 h-4" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'return_requested':
      case 'return requested':
        return <RotateCcw className="w-4 h-4" />;
      case 'return_approved':
      case 'return approved':
        return <RotateCcw className="w-4 h-4" />;
      case 'return_rejected':
      case 'return rejected':
        return <AlertCircle className="w-4 h-4" />;
      case 'returned':
        return <RotateCcw className="w-4 h-4" />;
      case 'inspection_pending':
      case 'inspection pending':
        return <Shield className="w-4 h-4" />;
      case 'inspection_passed':
      case 'inspection passed':
        return <CheckCircle className="w-4 h-4" />;
      case 'inspection_failed':
      case 'inspection failed':
        return <AlertCircle className="w-4 h-4" />;
      case 'partial_refund':
      case 'partial refund':
        return <RefreshCw className="w-4 h-4" />;
      case 'replacement_processing':
      case 'replacement processing':
        return <RefreshCw className="w-4 h-4" />;
      case 'refund_processing':
      case 'refund processing':
        return <RefreshCw className="w-4 h-4" />;
      case 'cancellation_requested':
      case 'cancellation requested':
      case 'cancel_requested':
      case 'cancel requested':
        return <AlertCircle className="w-4 h-4" />;
      case 'cancelled':
      case 'cancelled_(not_refunded)':
      case 'cancelled (not refunded)':
        return <X className="w-4 h-4" />;
      case 'returned_(no_refund)':
      case 'returned (no refund)':
      case 'returned_no_refund':
        return <X className="w-4 h-4" />;
      case 'refund':
      case 'refunded':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        // Handle capitalized versions as fallback
        switch (status) {
          case 'Placed':
            return <ShoppingBag className="w-4 h-4" />;
          case 'Processing':
            return <ShoppingBag className="w-4 h-4" />;
          case 'Printing Started':
            return <Printer className="w-4 h-4" />;
          case 'Production Completed':
            return <Zap className="w-4 h-4" />;
          case 'Quality Check':
            return <Shield className="w-4 h-4" />;
          case 'Packaging':
            return <ShoppingBag className="w-4 h-4" />;
          case 'Shipped':
            return <Truck className="w-4 h-4" />;
          case 'Delivered':
            return <CheckCircle className="w-4 h-4" />;
          case 'Completed':
            return <CheckCircle className="w-4 h-4" />;
          case 'Return Requested':
            return <RotateCcw className="w-4 h-4" />;
          case 'Return Approved':
            return <RotateCcw className="w-4 h-4" />;
          case 'Return Rejected':
            return <AlertCircle className="w-4 h-4" />;
          case 'Returned':
            return <RotateCcw className="w-4 h-4" />;
          case 'Inspection Pending':
            return <Shield className="w-4 h-4" />;
          case 'Inspection Passed':
            return <CheckCircle className="w-4 h-4" />;
          case 'Inspection Failed':
            return <AlertCircle className="w-4 h-4" />;
          case 'Partial Refund':
            return <RefreshCw className="w-4 h-4" />;
          case 'Replacement Processing':
            return <RefreshCw className="w-4 h-4" />;
          case 'Refund Processing':
            return <RefreshCw className="w-4 h-4" />;
          case 'Cancellation Requested':
            return <AlertCircle className="w-4 h-4" />;
          case 'Cancelled':
          case 'Cancelled (Not Refunded)':
            return <X className="w-4 h-4" />;
          case 'Returned (No Refund)':
            return <X className="w-4 h-4" />;
          case 'Refund':
          case 'Refunded':
            return <CheckCircle className="w-4 h-4" />;
          case 'Rejected':
            return <AlertCircle className="w-4 h-4" />;
          default:
            return <Package className="w-4 h-4" />;
        }
    }
  };

  const getTimelineColor = (status, isCompleted) => {
    if (!isCompleted) return 'text-gray-400 bg-gray-100';

    // Normalize status to handle case variations and underscores
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedStatus) {
      case 'placed':
        return 'text-teal-700 bg-teal-100';
      case 'processing':
        return 'text-blue-700 bg-blue-100';
      case 'printing_started':
      case 'printing started':
        return 'text-indigo-700 bg-indigo-100';
      case 'production_completed':
      case 'production completed':
        return 'text-cyan-700 bg-cyan-100';
      case 'quality_check':
      case 'quality check':
        return 'text-yellow-700 bg-yellow-100';
      case 'packaging':
        return 'text-amber-700 bg-amber-100';
      case 'shipped':
        return 'text-violet-700 bg-violet-100';
      case 'delivered':
        return 'text-emerald-700 bg-emerald-100';
      case 'completed':
        return 'text-emerald-700 bg-emerald-100';
      case 'return_requested':
      case 'return requested':
        return 'text-sky-700 bg-sky-100';
      case 'return_approved':
      case 'return approved':
        return 'text-teal-700 bg-teal-100';
      case 'return_rejected':
      case 'return rejected':
        return 'text-red-700 bg-red-100';
      case 'returned':
        return 'text-orange-700 bg-orange-100';
      case 'inspection_pending':
      case 'inspection pending':
        return 'text-purple-700 bg-purple-100';
      case 'inspection_passed':
      case 'inspection passed':
        return 'text-green-700 bg-green-100';
      case 'inspection_failed':
      case 'inspection failed':
        return 'text-red-700 bg-red-100';
      case 'partial_refund':
      case 'partial refund':
        return 'text-lime-700 bg-lime-100';
      case 'replacement_processing':
      case 'replacement processing':
        return 'text-sky-700 bg-sky-100';
      case 'refund_processing':
      case 'refund processing':
        return 'text-indigo-700 bg-indigo-100';
      case 'cancellation_requested':
      case 'cancellation requested':
      case 'cancel_requested':
      case 'cancel requested':
        return 'text-rose-700 bg-rose-100';
      case 'cancelled':
      case 'cancelled_(not_refunded)':
      case 'cancelled (not refunded)':
        return 'text-red-700 bg-red-100';
      case 'returned_(no_refund)':
      case 'returned (no refund)':
      case 'returned_no_refund':
        return 'text-red-700 bg-red-100';
      case 'refund':
      case 'refunded':
        return 'text-green-700 bg-green-100';
      case 'rejected':
        return 'text-rose-700 bg-rose-100';
      default:
        // Handle capitalized versions as fallback
        switch (status) {
          case 'Placed':
            return 'text-teal-700 bg-teal-100';
          case 'Processing':
            return 'text-blue-700 bg-blue-100';
          case 'Printing Started':
            return 'text-indigo-700 bg-indigo-100';
          case 'Production Completed':
            return 'text-cyan-700 bg-cyan-100';
          case 'Quality Check':
            return 'text-yellow-700 bg-yellow-100';
          case 'Packaging':
            return 'text-amber-700 bg-amber-100';
          case 'Shipped':
            return 'text-violet-700 bg-violet-100';
          case 'Delivered':
            return 'text-emerald-700 bg-emerald-100';
          case 'Completed':
            return 'text-emerald-700 bg-emerald-100';
          case 'Return Requested':
            return 'text-sky-700 bg-sky-100';
          case 'Return Approved':
            return 'text-teal-700 bg-teal-100';
          case 'Return Rejected':
            return 'text-red-700 bg-red-100';
          case 'Returned':
            return 'text-orange-700 bg-orange-100';
          case 'Inspection Pending':
            return 'text-purple-700 bg-purple-100';
          case 'Inspection Passed':
            return 'text-green-700 bg-green-100';
          case 'Inspection Failed':
            return 'text-red-700 bg-red-100';
          case 'Partial Refund':
            return 'text-lime-700 bg-lime-100';
          case 'Replacement Processing':
            return 'text-sky-700 bg-sky-100';
          case 'Refund Processing':
            return 'text-indigo-700 bg-indigo-100';
          case 'Cancellation Requested':
            return 'text-rose-700 bg-rose-100';
          case 'Cancelled':
          case 'Cancelled (Not Refunded)':
            return 'text-red-700 bg-red-100';
          case 'Returned (No Refund)':
            return 'text-red-700 bg-red-100';
          case 'Refund':
          case 'Refunded':
            return 'text-green-700 bg-green-100';
          case 'Rejected':
            return 'text-rose-700 bg-rose-100';
          default:
            return 'text-gray-600 bg-gray-100';
        }
    }
  };

  if (!isOpen || !order) return null;

  const subtotal = order.subtotal || calculateSubtotal();
  const shipping = order.shippingCharges || 0;
  const tax = order.tax || 0;
  const promoDiscount = order.promoDiscount || 0;
  const total = order.total || 0;

  const canTrack = order.status === 'Shipped' || order.status === 'Delivered';
  const canReorder = ['Delivered', 'Completed', 'Cancelled', 'Refund', 'Refunded', 'Returned (No Refund)', 'returned_no_refund'].includes(order.status);
  const canDownload = order.status === 'Delivered' || order.status === 'Completed';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className="w-full max-w-6xl max-h-[90vh] overflow-hidden bg-white shadow-2xl rounded-2xl transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-orange-50">
            <div>
              <h2 id="modal-title" className="text-2xl font-bold text-gray-900">
                Order Details
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Order {order.orderNumber} • {formatDate(order.date)}
              </p>
              {order.hasUnavailableItems && (
                <div className="mt-2 flex items-center gap-2 text-sm text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
                  <AlertTriangle className="w-4 h-4" />
                  Some items are no longer available
                </div>
              )}
              {order.promoCode && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                  <Tag className="w-4 h-4" />
                  Promo code {order.promoCode} applied
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <OrderStatusBadge status={order.status} size="large" />
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-3 p-6">
              {/* Left Column - Order Items & Timeline */}
              <div className="lg:col-span-2 space-y-6">
                {/* Order Items */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-teal-600" />
                    Order Items ({order?.items?.length || 0})
                  </h3>

                  <div className="space-y-4">
                    {(order?.items || []).map((item) => (
                      <div key={item.id} className={`flex gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${item.isUnavailable ? 'border-l-4 border-yellow-400' : ''}`}>
                        <div className="relative">
                          <img
                            src={item.image}
                            alt={item.name}
                            className={`w-20 h-20 object-cover rounded-lg ${item.isUnavailable ? 'opacity-50 grayscale' : ''}`}
                            loading="lazy"
                          />
                          {item.quantity > 1 && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {item.quantity}
                            </div>
                          )}
                          {item.isUnavailable && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                              <AlertTriangle className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {item.customization && !item.isUnavailable && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-orange-400 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <h4 className={`font-semibold ${item.isUnavailable ? 'text-gray-500' : 'text-gray-900'}`}>
                            {item.name}
                            {item.isUnavailable && (
                              <span className="ml-2 text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                                No longer available
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">{item.category}</p>

                          {item.customization && !item.isUnavailable && (
                            <div className="text-xs text-gray-500 space-y-1 mb-2">
                              {item.customization.text && (
                                <p>Custom Text: "{item.customization.text}"</p>
                              )}
                              {item.customization.color && (
                                <p className="flex items-center gap-2">
                                  Color:
                                  <span
                                    className="w-3 h-3 rounded-full border border-gray-300"
                                    style={{ backgroundColor: item.customization.color }}
                                  ></span>
                                  {item.customization.color}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Qty: {item.quantity}
                            </span>
                            <div className="text-right">
                              {item.originalPrice && item.isOnSale && (
                                <span className="text-sm text-gray-500 line-through mr-2">
                                  ₹{item.originalPrice.toFixed(2)}
                                </span>
                              )}
                              <span className={`font-semibold ${item.isOnSale ? 'text-red-600' : 'text-gray-900'}`}>
                                ₹{(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Order Timeline */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    Order Timeline
                  </h3>

                  <div className="space-y-4">
                    {(order?.timeline || []).map((event, index) => {
                      const isCompleted = true; // All timeline events shown are completed
                      const isLast = index === order.timeline.length - 1;

                      return (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTimelineColor(event.status, isCompleted)}`}>
                              {getTimelineIcon(event.status)}
                            </div>
                            {!isLast && (
                              <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>
                            )}
                          </div>

                          <div className="flex-1 pb-8">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-gray-900 capitalize">
                                  {event.status === 'Placed' ? 'Order Placed' :
                                   event.status === 'Processing' ? 'Awaiting Process' :
                                   event.status === 'Packaging' ? 'Awaiting Process' :
                                   event.status === 'Quality Check' || event.status === 'quality_check' ? 'Awaiting Process' :
                                   event.status === 'return_requested' ? 'Return Requested' :
                                   event.status === 'cancel_requested' ? 'Cancel Requested' :
                                   event.status === 'cancellation_requested' ? 'Cancellation Requested' :
                                   event.status === 'Returned' ? 'Received Product' :
                                   event.status === 'returned_no_refund' ? 'Returned (No Refund)' :
                                   event.status === 'inspection_pending' ? 'Inspection Pending' :
                                   event.status === 'replacement_processing' ? 'Replacement Processing' :
                                   event.status === 'partial_refund' ? 'Partial Refund' :
                                   event.status === 'refund_processing' ? 'Refund Processing' :
                                   event.status === 'Refunded' ? 'Refunded' :
                                   event.status.replace(/_/g, ' ')}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  {event.description}
                                </p>
                              </div>
                              <div className="text-right text-sm text-gray-500">
                                <div>{formatDate(event.date)}</div>
                                <div>{formatTime(event.time)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Status-specific messages */}
                  {order.status === 'Cancelled' && order.cancellationReason && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Cancellation Reason</h4>
                      <p className="text-sm text-red-700">{order.cancellationReason}</p>
                    </div>
                  )}

                  {(order.status === 'Return Requested' || order.status === 'return_requested') && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2">Return Information</h4>
                      <p className="text-sm text-amber-700">This order has a return request and is being processed.</p>
                    </div>
                  )}

                  {(order.status === 'Cancellation Requested' || order.status === 'cancellation_requested' || order.status === 'cancel_requested') && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Cancellation Information</h4>
                      <p className="text-sm text-red-700">This order has a cancellation request and is being reviewed by our team.</p>
                    </div>
                  )}

                  {order.status === 'Returned' && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h4 className="font-medium text-orange-800 mb-2">Return Completed</h4>
                      <p className="text-sm text-orange-700">This order has been returned successfully.</p>
                    </div>
                  )}

                  {(order.status === 'Refund Processing' || order.status === 'refund_processing') && (
                    <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h4 className="font-medium text-indigo-800 mb-2">Refund Processing</h4>
                      <p className="text-sm text-indigo-700">Your refund is being processed and will be credited to your account soon.</p>
                    </div>
                  )}

                  {(order.status === 'Refund' || order.status === 'Refunded') && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">Refund Processed</h4>
                      <p className="text-sm text-green-700">Your refund has been processed and will be credited to your account.</p>
                    </div>
                  )}

                  {(order.status === 'Returned (No Refund)' || order.status === 'returned_no_refund') && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Return Processed - No Refund</h4>
                      <p className="text-sm text-red-700">Your return has been processed but no refund will be issued.</p>
                    </div>
                  )}
                </section>
              </div>

              {/* Right Column - Order Summary & Actions */}
              <div className="space-y-6">
                {/* Order Summary */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>
                    {/* <div className="flex justify-between">
                      <span>Shipping:</span>
                      <span>₹{shipping.toFixed(2)}</span>
                    </div> */}
                    {promoDiscount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          Promo Discount {order.promoCode && `(${order.promoCode})`}:
                        </span>
                        <span>-₹{promoDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>GST:</span>
                      <span>₹{tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span className="text-teal-700">₹{total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Promo Code Information */}
                  {order.promoCode && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                        <Tag className="w-4 h-4" />
                        Promo Code Applied
                      </div>
                      <div className="text-sm text-green-700">
                        <div className="font-medium">{order.promoCode}</div>
                        <div>You saved ₹{promoDiscount.toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                </section>

                {/* Billing Address */}
                {order?.billingAddress && (
                  <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-teal-600" />
                      Billing Address
                    </h3>

                    <div className="text-sm text-gray-700 space-y-2">
                      <div className="font-medium">
                        {order.billingAddress.firstName} {order.billingAddress.lastName}
                      </div>
                      {order.billingAddress.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          {order.billingAddress.email}
                        </div>
                      )}
                      {order.billingAddress.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          {order.billingAddress.phone}
                        </div>
                      )}
                      <div>{order.billingAddress.address}</div>
                      <div>
                        {order.billingAddress.city}, {order.billingAddress.state} {order.billingAddress.pincode}
                      </div>
                    </div>
                  </section>
                )}

                {/* Shipping Address */}
                <section className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    Shipping Address
                  </h3>

                  <div className="text-sm text-gray-700 space-y-1">
                    <div className="font-medium">{order?.shippingAddress?.name}</div>
                    <div>{order.shippingAddress.street}</div>
                    <div>
                      {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                    </div>
                  </div>
                </section>

                {/* Action Buttons */}
                <section className="space-y-3">
                  {canReorder && (
                    <button
                      onClick={() => {
                        onReorder(order);
                        onClose();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Reorder Items
                    </button>
                  )}

                  {canDownload && (
                    <button
                      onClick={handleDownloadInvoice}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-medium"
                    >
                      <Download className="w-5 h-5" />
                      Download Invoice
                    </button>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailsModal;
